// src/index.tsx
import './setupProduction';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import store from './store';
import { loadUser } from './features/auth/authSlice';
import { useAppDispatch } from './hooks';
import { getToken, getCurrentAuthContext } from './services/auth';
import { BrandProvider } from './brands/BrandProvider';
import { resolveBrandKey } from './brands/registry';
import './index.css';

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function BrandAwareTheme({ children }: { children: React.ReactNode }) {
  const hostKey = resolveBrandKey();
  const themed = React.useMemo(
    () =>
      hostKey === 'zuadaria'
        ? createTheme(theme, {
            palette: {
              primary: {
                main: '#B84D4D',
                light: '#D46A6A',
                dark: '#8F2F2F',
                contrastText: '#fff',
              },
              secondary: {
                main: '#E6A532',
                light: '#F2B84A',
                dark: '#C48A28',
                contrastText: '#1A1515',
              },
              background: {
                default: '#F7F5F3',
                paper: '#fff',
              },
            },
          })
        : theme,
    [hostKey]
  );

  return (
    <ThemeProvider theme={themed}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#4b1b7a',
      light: '#6c3aa1',
      dark: '#321153',
      contrastText: '#fff',
    },
    secondary: {
      main: '#e9edf5',
      light: '#f3f6fb',
      dark: '#d7deea',
      contrastText: '#243042',
    },
    background: {
      default: '#f1f5fb',
      paper: '#fff',
    },
    text: {
      primary: '#1f2735',
      secondary: '#5a6474',
    },
  },
  typography: {
    fontFamily: ['"Manrope"', '"Segoe UI"', '"Arial"', 'sans-serif'].join(','),
    h1: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
    h6: { fontFamily: '"Sora", "Manrope", sans-serif', fontWeight: 600, letterSpacing: '-0.02em' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 13 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.08)',
          transition: 'all 0.2s ease',
          '&:hover': { boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.12)' },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 6px 18px rgba(15, 23, 42, 0.08)',
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16,
        },
      },
    },
  },
});

// Componente que dispara o loadUser() uma vez no início
const AppInitializer: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const context = getCurrentAuthContext();
    const token = getToken(context);
    if (token) {
      dispatch(loadUser());
    }
  }, [dispatch]);

  return <App />;
};

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <BrandProvider>
            <BrandAwareTheme>
              <AppInitializer />
            </BrandAwareTheme>
          </BrandProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>
);
